import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { chatApi } from "apis";
import { SecureStorageKeys } from "constants/secureStorage";
import { ConversationListResponseDto } from "libs/cellera-sdk";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Socket } from "socket.io-client";
import { ChatSubscribeMessage } from "types/socket";
import { disconnectSocket, initChatSocket } from "utils/chatSocket";
import { secureStorage } from "utils/secureStorage";

type ChatSocketContextValue = {
  socket: Socket | null;
  isConnected: boolean;
};

export const ChatSocketContext = createContext<ChatSocketContextValue>({
  socket: null,
  isConnected: false,
});

export function ChatSocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();

  // Function to join all conversation rooms
  const joinAllConversationRooms = useCallback(
    async (s: Socket) => {
      // First check if conversations data exists
      let conversationsData = queryClient.getQueryData<
        InfiniteData<ConversationListResponseDto>
      >(["conversations"]);

      // If data doesn't exist, fetch it first
      if (!conversationsData?.pages) {
        try {
          await queryClient.prefetchInfiniteQuery({
            queryKey: ["conversations"],
            queryFn: ({ pageParam = 1 }) =>
              chatApi.getConversations({
                page: pageParam as number,
                pageSize: 100,
              }),
            initialPageParam: 1,
          });

          // Get the data after prefetching
          conversationsData = queryClient.getQueryData<
            InfiniteData<ConversationListResponseDto>
          >(["conversations"]);
        } catch (error) {
          console.error("Failed to fetch conversations:", error);
          return;
        }
      }

      if (!conversationsData?.pages) {
        return;
      }

      const conversations = conversationsData.pages.flatMap(
        (page) => page.conversations || []
      );

      if (conversations.length === 0) {
        return;
      }

      conversations.forEach((conversation) => {
        s.emit(ChatSubscribeMessage.JOIN_CONVERSATION, {
          conversationId: conversation.id,
        });
      });

      console.log(
        `Joined ${conversations.length} conversation rooms for global notifications`
      );
    },
    [queryClient]
  );

  useEffect(() => {
    const token = secureStorage.get(SecureStorageKeys.CUSTOM_TOKEN);
    if (!token) {
      console.warn("chat socket missing auth token");
      return;
    }

    const s = initChatSocket(token);

    const handleConnect = () => {
      setIsConnected(true);
      // Join all conversation rooms to receive notifications (async)
      void joinAllConversationRooms(s);
    };

    const handleDisconnect = () => setIsConnected(false);

    s.on("connect", handleConnect);
    s.on("disconnect", handleDisconnect);

    setSocket(s);

    return () => {
      s.off("connect", handleConnect);
      s.off("disconnect", handleDisconnect);
      disconnectSocket();
    };
  }, [joinAllConversationRooms]);

  // Listen for conversations query updates and join new rooms
  useEffect(() => {
    if (!socket || !isConnected) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.query?.queryKey?.[0] === "conversations") {
        // When conversations data is updated, join all rooms (async)
        void joinAllConversationRooms(socket);
      }
    });

    return unsubscribe;
  }, [socket, isConnected, queryClient, joinAllConversationRooms]);

  const value = useMemo(() => ({ socket, isConnected }), [socket, isConnected]);

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
}
